import { css } from '@emotion/react'
import { Button, Cell, Grid, HFlow, Icon, Tooltip } from 'bold-ui'
import { useAcessoLotacaoOrEstagio } from 'components/auth/useAcessoLotacao'
import { Box } from 'components/Box'
import { CheckboxField, EquipeSelectField, Form, FormRenderProps, SubmitButton } from 'components/form'
import { DateRangeField } from 'components/form/field/daterange/DateRangeField'
import { RecordStringSelectField } from 'components/form/final-form/RecordStringSelectField/RecordStringSelectField'
import { OrigemDadoEnumRecord } from 'components/origem-label-tag/OrigemRegistroInfo'
import { useServerTime } from 'hooks/useServerTime'
import { metaPath } from 'util/metaPath'
import {
  AtividadeColetivaFilterFormModel,
  AtividadeColetivaFilterPopperFormModel,
  AtividadeColetivaFonteEnum,
  tipoAtividadeRecord,
  TIPOS_ATIVIDADE_EM_SAUDE,
  TIPOS_ATIVIDADE_REUNIAO,
} from 'view/atividade-coletiva/model-atividadeColetiva'
import { getFiltroPadrao, isCleanFilter } from 'view/atividade-coletiva/utils-atividadeColetiva'

const path = metaPath<AtividadeColetivaFilterPopperFormModel>()

interface AtividadeColetivaFilterPopperFormProps {
  filter: AtividadeColetivaFilterFormModel
  onChangeFilter: (values: AtividadeColetivaFilterFormModel) => void
  onClose: () => void
}

const origemAtendimentoStringRecord = Object.fromEntries(
  Object.entries(OrigemDadoEnumRecord).map(([key, value]) => [key, value.nome])
) as Record<string, string>

export function AtividadeColetivaFilterPopperForm(props: AtividadeColetivaFilterPopperFormProps) {
  const { filter, onChangeFilter, onClose } = props
  const { getServerTimeNow } = useServerTime()
  const serverTime = getServerTimeNow()
  const { acesso } = useAcessoLotacaoOrEstagio()

  const municipio = acesso?.municipio

  const handleClear = () => onChangeFilter(getFiltroPadrao(filter))

  const handleSubmit = (values: AtividadeColetivaFilterPopperFormModel) => {
    onChangeFilter({ ...filter, ...values })
    onClose()
  }

  const isFonteOutros = filter.fonteRegistros === AtividadeColetivaFonteEnum.OUTROS

  const renderForm = (formProps: FormRenderProps<AtividadeColetivaFilterPopperFormModel>) => {
    return (
      <Box style={styles.box}>
        <Grid gap={1}>
          <Cell size={12}>
            <RecordStringSelectField
              name={path.tiposAtividade}
              label='Tipo de atividade'
              record={tipoAtividadeRecord}
              multiple
              subset={filter.isReuniao ? TIPOS_ATIVIDADE_REUNIAO : TIPOS_ATIVIDADE_EM_SAUDE}
            />
          </Cell>

          <Cell size={12}>
            <HFlow alignItems='flex-end'>
              <DateRangeField name={path.periodo} label='Período do registro' maxDate={serverTime} />
            </HFlow>
          </Cell>

          <Cell size={12}>
            <EquipeSelectField name={path.equipes} label='Equipe' localidadeId={municipio.id} multiple />
          </Cell>

          <Cell size={12}>
            <Tooltip
              text={!isFonteOutros ? 'Registros da fonte Atividade coletiva são registros de origem única.' : ''}
            >
              <RecordStringSelectField
                name={path.origens}
                label='Origem'
                record={origemAtendimentoStringRecord}
                multiple
                disabled={!isFonteOutros}
              />
            </Tooltip>
          </Cell>

          <Cell size={12}>
            <Tooltip text={isFonteOutros ? 'Registros de outras fontes são registros já processados.' : ''}>
              <div>
                <CheckboxField
                  name={path.isProcessado}
                  label='Ver somente registros já processados'
                  disabled={isFonteOutros}
                  {...(isFonteOutros ? { checked: false } : {})}
                />
              </div>
            </Tooltip>
          </Cell>

          <Cell size={12}>
            <HFlow justifyContent='center'>
              <Button onClick={onClose} size='small' skin='outline' kind='normal'>
                Fechar
              </Button>

              <Tooltip text='Redefinir filtros para o padrão'>
                <Button kind='normal' size='small' onClick={handleClear} disabled={isCleanFilter(filter)}>
                  <Icon style={styles.redoIcon} icon='redo' />
                  Voltar para padrão
                </Button>
              </Tooltip>

              <SubmitButton size='small' handleSubmit={formProps.handleSubmit}>
                Filtrar
              </SubmitButton>
            </HFlow>
          </Cell>
        </Grid>
      </Box>
    )
  }

  return (
    <Form<AtividadeColetivaFilterPopperFormModel> initialValues={filter} render={renderForm} onSubmit={handleSubmit} />
  )
}

const styles = {
  box: css`
    width: 21.75rem;
    margin-top: 1rem;
  `,
  redoIcon: css`
    padding-right: 0.5rem;
  `,
}
